import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Briefcase,
  CalendarDays,
  CheckCircle2,
  Heart,
  Laptop,
  Loader2,
  MapPin,
  Mail,
  Smartphone,
  Sparkles,
  UserCircle,
  Users as UsersIcon,
  Wrench,
} from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { apiClient } from '@/services/api-client';
import type {
  DevicePlatform,
  Gender,
  MaritalStatus,
  TechComfort,
  UserDetail,
  UserProfile,
} from '@/types';

/**
 * Shared user-profile detail dialog. Used by:
 *   1. Users page "View Details" action
 *   2. Life-Story Interview page "View User Profile" CTA
 *
 * Fetches the full user record (core + `users.profile` JSONB + full
 * persona row) lazily when opened, keyed by userId so React Query de-dups
 * across callers. Designed as a wide, read-only panel — the founder
 * wanted a single place to see *everything* we know about a test user,
 * not a navigate-away page.
 */
interface UserDetailDialogProps {
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const userDetailQueryKey = (userId: string) => ['admin', 'users', 'detail', userId] as const;

async function fetchUserDetail(userId: string): Promise<UserDetail> {
  const res = await apiClient.get<UserDetail>(`/api/v1/admin/users/${userId}`);
  if (!res.success || !res.data) {
    throw new Error(res.error?.message ?? 'Failed to load user details');
  }
  return res.data as unknown as UserDetail;
}

export function UserDetailDialog({ userId, open, onOpenChange }: UserDetailDialogProps) {
  const {
    data: user,
    isLoading,
    error,
  } = useQuery<UserDetail, Error>({
    queryKey: userDetailQueryKey(userId ?? ''),
    queryFn: () => fetchUserDetail(userId as string),
    enabled: open && !!userId,
    staleTime: 30_000,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">User Profile</DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="flex min-h-[12rem] items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Loading profile…
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {error.message}
          </div>
        )}

        {user && !isLoading && <UserDetailBody user={user} />}
      </DialogContent>
    </Dialog>
  );
}

// ── Body ────────────────────────────────────────────────────────────────────

function UserDetailBody({ user }: { user: UserDetail }) {
  const fullName =
    user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.firstName || user.lastName || 'No name set';

  return (
    <div className="space-y-6">
      {/* Identity header */}
      <div className="flex items-start gap-4">
        <Avatar className="h-20 w-20">
          <AvatarImage src={user.profileImageUrl || undefined} />
          <AvatarFallback className="bg-orb-gold/10 text-2xl text-orb-gold">
            {user.firstName?.[0] || user.email[0].toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-xl font-semibold text-foreground">{fullName}</h3>
          <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Mail className="h-3.5 w-3.5" />
            {user.email}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge
              variant={
                user.role === 'super_admin'
                  ? 'destructive'
                  : user.role === 'admin'
                    ? 'default'
                    : 'secondary'
              }
            >
              {user.role.replace(/_/g, ' ')}
            </Badge>
            <Badge variant={user.isDisabled ? 'destructive' : 'success'}>
              {user.isDisabled ? 'Disabled' : 'Active'}
            </Badge>
            {user.persona && (
              <Badge className="bg-orb-gold/15 text-orb-gold hover:bg-orb-gold/20">
                <UserCircle className="mr-1 h-3 w-3" />
                {user.persona.name}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <Separator />

      {/* Primary grid: Profile + Persona */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ProfileSection profile={user.profile} />
        <PersonaSection persona={user.persona} />
      </div>

      {/* Narrative — full width since it's long-form */}
      {user.profile?.lifeJourney && (
        <>
          <Separator />
          <Section title="Life Journey" icon={<Sparkles className="h-4 w-4 text-orb-gold" />}>
            <p className="whitespace-pre-line text-sm leading-relaxed text-foreground">
              {user.profile.lifeJourney}
            </p>
          </Section>
        </>
      )}

      <Separator />

      {/* Account metadata */}
      <Section title="Account" icon={<CalendarDays className="h-4 w-4 text-muted-foreground" />}>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
          <Field label="User ID" value={<code className="text-[11px]">{user.id}</code>} />
          <Field label="Clerk ID" value={<code className="text-[11px]">{user.clerkId}</code>} />
          <Field label="Joined" value={format(new Date(user.createdAt), 'PPP')} />
          <Field label="Last Updated" value={format(new Date(user.updatedAt), 'PPP')} />
          <Field
            label="Onboarding"
            value={
              user.onboardingCompletedAt ? (
                <span className="inline-flex items-center gap-1 text-orb-teal">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {format(new Date(user.onboardingCompletedAt), 'PP')}
                </span>
              ) : (
                <span className="text-muted-foreground">Not completed</span>
              )
            }
          />
          <Field
            label="Terms Accepted"
            value={user.termsAcceptedAt ? format(new Date(user.termsAcceptedAt), 'PP') : '—'}
          />
        </dl>
      </Section>
    </div>
  );
}

// ── Profile section ─────────────────────────────────────────────────────────

function ProfileSection({ profile }: { profile: UserProfile | null }) {
  if (!profile) {
    return (
      <Section title="Profile" icon={<UserCircle className="h-4 w-4 text-muted-foreground" />}>
        <p className="text-sm italic text-muted-foreground">No extended profile on file.</p>
      </Section>
    );
  }

  const d = profile.demographics;
  const hasDemographics = d && (d.age || d.gender || d.location || d.maritalStatus || d.family);

  return (
    <Section title="Profile" icon={<UserCircle className="h-4 w-4 text-muted-foreground" />}>
      <div className="space-y-4">
        {hasDemographics && (
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            {d?.age != null && <Field label="Age" value={`${d.age}`} />}
            {d?.gender && <Field label="Gender" value={prettyGender(d.gender)} />}
            {d?.location && (
              <Field
                label="Location"
                value={
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                    {d.location}
                  </span>
                }
              />
            )}
            {d?.maritalStatus && (
              <Field
                label="Marital Status"
                value={
                  <span>
                    {prettyMaritalStatus(d.maritalStatus)}
                    {d.maritalStatusNotes ? (
                      <span className="block text-xs text-muted-foreground">
                        {d.maritalStatusNotes}
                      </span>
                    ) : null}
                  </span>
                }
              />
            )}
            {d?.family && (
              <div className="sm:col-span-2">
                <FieldLabel>Family</FieldLabel>
                <p className="mt-1 flex items-start gap-1.5 text-sm text-foreground">
                  <UsersIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span>{d.family}</span>
                </p>
              </div>
            )}
          </dl>
        )}

        {profile.occupation && (
          <div>
            <FieldLabel>Occupation</FieldLabel>
            <p className="mt-1 flex items-start gap-1.5 text-sm text-foreground">
              <Briefcase className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span>{profile.occupation}</span>
            </p>
          </div>
        )}

        {profile.devices && profile.devices.length > 0 && (
          <div>
            <FieldLabel>Devices</FieldLabel>
            <ul className="mt-1 space-y-1">
              {profile.devices.map((device, i) => (
                <li
                  key={`${device.label}-${i}`}
                  className="flex items-center gap-2 text-sm text-foreground"
                >
                  {deviceIcon(device.platform)}
                  <span>{device.label}</span>
                  <Badge variant="outline" className="text-[10px] uppercase">
                    {device.platform}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>
        )}

        {(profile.techComfort || profile.techComfortNotes) && (
          <div>
            <FieldLabel>Tech Comfort</FieldLabel>
            <p className="mt-1 flex items-start gap-1.5 text-sm text-foreground">
              <Wrench className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span>
                {profile.techComfort ? prettyTechComfort(profile.techComfort) : '—'}
                {profile.techComfortNotes ? (
                  <span className="block text-xs text-muted-foreground">
                    {profile.techComfortNotes}
                  </span>
                ) : null}
              </span>
            </p>
          </div>
        )}

        {profile.motivations?.top && (
          <div>
            <FieldLabel>Top Motivation</FieldLabel>
            <p className="mt-1 text-sm text-foreground">{profile.motivations.top}</p>
          </div>
        )}

        {profile.motivations?.painPoint && (
          <div>
            <FieldLabel>Pain Point</FieldLabel>
            <p className="mt-1 text-sm text-foreground">{profile.motivations.painPoint}</p>
          </div>
        )}

        {!hasDemographics &&
          !profile.occupation &&
          (!profile.devices || profile.devices.length === 0) &&
          !profile.techComfort &&
          !profile.motivations?.top &&
          !profile.motivations?.painPoint && (
            <p className="text-sm italic text-muted-foreground">No extended profile on file.</p>
          )}
      </div>
    </Section>
  );
}

// ── Persona section ─────────────────────────────────────────────────────────

function PersonaSection({ persona }: { persona: UserDetail['persona'] }) {
  if (!persona) {
    return (
      <Section title="Persona" icon={<Heart className="h-4 w-4 text-muted-foreground" />}>
        <p className="text-sm italic text-muted-foreground">No persona assigned.</p>
      </Section>
    );
  }

  return (
    <Section title="Persona" icon={<Heart className="h-4 w-4 text-orb-gold" />}>
      <div className="space-y-3">
        <div>
          <h4 className="font-display text-base font-semibold text-foreground">{persona.name}</h4>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{persona.code}</p>
        </div>

        {persona.attributes && (
          <div>
            <FieldLabel>Attributes</FieldLabel>
            <p className="mt-1 whitespace-pre-line text-sm text-foreground">{persona.attributes}</p>
          </div>
        )}

        {persona.keyMotivations && (
          <div>
            <FieldLabel>Key Motivations</FieldLabel>
            <p className="mt-1 whitespace-pre-line text-sm text-foreground">
              {persona.keyMotivations}
            </p>
          </div>
        )}

        {persona.painPoints && (
          <div>
            <FieldLabel>Pain Points</FieldLabel>
            <p className="mt-1 whitespace-pre-line text-sm text-foreground">{persona.painPoints}</p>
          </div>
        )}
      </div>
    </Section>
  );
}

// ── Primitives ──────────────────────────────────────────────────────────────

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h4 className="mb-3 flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {icon}
        {title}
      </h4>
      {children}
    </section>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{children}</p>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className="mt-1 text-sm text-foreground">{value}</div>
    </div>
  );
}

function deviceIcon(platform: DevicePlatform) {
  if (platform === 'ios' || platform === 'android') {
    return <Smartphone className="h-3.5 w-3.5 text-muted-foreground" />;
  }
  return <Laptop className="h-3.5 w-3.5 text-muted-foreground" />;
}

// Label formatters — kept close to the component since they are only
// used for presentation and the enum values are short.
function prettyGender(g: Gender): string {
  return g === 'non_binary'
    ? 'Non-binary'
    : g === 'prefer_not_to_say'
      ? 'Prefer not to say'
      : g.charAt(0).toUpperCase() + g.slice(1);
}

function prettyMaritalStatus(m: MaritalStatus): string {
  if (m === 'prefer_not_to_say') return 'Prefer not to say';
  return m.charAt(0).toUpperCase() + m.slice(1);
}

function prettyTechComfort(t: TechComfort): string {
  switch (t) {
    case 'low':
      return 'Low';
    case 'medium':
      return 'Medium';
    case 'medium_high':
      return 'Medium–High';
    case 'high':
      return 'High';
  }
}
